
import {defineComponent} from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import {FilterMatchMode} from 'primevue/api';
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import SpecimenLabelPrinter from "@/components/picker/SpecimenLabelPrinter.vue";
import MultiSelect from "primevue/multiselect";
import {
  checkUserAdminRoleURL,
  defaultHeadersUserRecords,
  getAllUserRecordsURL,
  getUserRecordsURL
} from "@/config/app-settings";
import ConfirmPopup from "primevue/confirmpopup";
import Message from "primevue/message";
import Image from "primevue/image";
import axios from "axios";
import LichenRecordMapPopup from "@/classes/LichenRecordMapPopup";
import {debounce} from "lodash";


export default defineComponent({
  name: "Records",
  components: {DataTable, Column, Button, InputText, MultiSelect, ConfirmPopup, Message, Image, SpecimenLabelPrinter},
  data() {
    return {
      loading: false,
      features: [] as LichenRecordMapPopup[],
      headers: [] as string[],
      totalRecords: 0,
      lazyParams: {},
      inputText: '' as string,
      warningComment: false as Boolean,
      filters: {
        'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
      },
      allHeaders: [] as any[],
      csvHeaders: [] as any[],
      selectedHeaders: [] as any[],
      state: 'init' as 'init' | 'submitted' | 'success' | 'error',
    }
  },
  props: {
    editable: Boolean,
  },
  computed: {
    lang(): String {
      return this.$store.state.language
    }
  },
  watch: {
    lang() {
      this.loadData()
    },
    selectedHeaders() {
      defaultHeadersUserRecords.forEach((el, i) => {
        if (!this.selectedHeaders.some((e: any) => e.header === el.header)) {
          this.selectedHeaders.splice(i, 0, {name: el.name, header: el.header});
        }
      })
      this.$store.commit('setSelectedRecordHeaders', this.selectedHeaders)
    },
    inputText() {
      this.onFilter()
    },
  },
  methods: {
    showColumn(column: string) {
      if (this.selectedHeaders.map((e: any) => {
        return e.header
      }).includes(column)) {
        return true
      } else {
        return false
      }
    },
    editItem(item: any) {
      this.$store.commit('setFormState', 'editing')
      this.$store.dispatch('edit', item)
    },
    toForm() {
      this.$store.commit('setFormState', 'new')
      this.$router.push({name: 'Form'})
    },
    printSpecimenLabel(data: any) {
      // @ts-ignore
      this.$refs.SpecimenLabelPrinter.printSpecimenLabel(data);

    },
    loadAvailableHeaders() {
      // load default headers for csv-export
      this.csvHeaders = this.headers.map((h: any) => {
        return {name: this.$t('table.' + h), header: h}
      })

      this.allHeaders = this.headers.map((h: any) => {
        return {name: 'table.' + h, header: h}
      })

      // load headers from browser session, or restore default headers if a new version has been deployed
      this.selectedHeaders = (this.$store.state.selectedRecordHeaders && this.$store.state.selectedRecordHeaders.every((el: any) => this.headers.includes(el.header))) ? this.$store.state.selectedRecordHeaders : this.headers.map((h: any) => {
        return {name: 'table.' + h, header: h}
      })

      // should not be displayed to user
      this.allHeaders = this.allHeaders.filter((el: any) => el.header !== "CERTIFICATES" && el.header !== "ADM_COMMENT" && el.header !== "ADM_VALIDATION_STATUS");
      this.selectedHeaders = this.selectedHeaders.filter((el: any) => el.header !== "CERTIFICATES" && el.header !== "ADM_COMMENT" && el.header !== "ADM_VALIDATION_STATUS");

    },
    deleteRecord(event: any, recordId: number) {
      this.$confirm.require({
        target: event.currentTarget,
        message: this.$t('form.confirmFinalDeletion'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.$store.commit('finalDeletionOfLichenRecord', recordId)
          this.state = 'success'
          this.loadData()
        }
      });
    },
    processRecords(res: any) {
      this.features = res.data.content
      this.totalRecords = res.data.totalElements

      if (this.features[0]) {
        this.headers = Object.keys(this.features[0])
        this.allHeaders = this.headers.map((h: any) => {
          return {name: 'table.' + h, header: h}
        })
        this.selectedHeaders = this.$store.state.selectedRecordHeaders ? this.$store.state.selectedRecordHeaders : this.headers.map((h: any) => {
          return {name: 'table.' + h, header: h}
        })
      } else {
        // clear headers from browser session
        this.headers = []
        this.$store.state.selectedRecordHeaders = []
      }
      this.loadAvailableHeaders()
      this.loading = false

    },

    loadData() {
      this.loading = true;

      // check if logged in user has admin role and show all records
      axios.get(checkUserAdminRoleURL).then(res => {
        if (res.data == true) {
          axios.post(getAllUserRecordsURL, this.lazyParams)
              .then(res => {
                this.processRecords(res)
              })
              .finally(() => this.loading = false)
        } else {
          axios.post(getUserRecordsURL, this.lazyParams)
              .then(res => {
                this.processRecords(res)
              })
              .finally(() => this.loading = false)
        }
      })
    },
    exportCsv() {
      // exclude internal admin comment and image base64 string representation from being printed to csv export
      const exportableCSVHeaders = this.allHeaders.filter((el: any) => el.header !== "IMAGE" && el.header !== "ADM_COMMENT").map((h: any) => {
        return h.header
      })

      const rows = [
        exportableCSVHeaders
      ];


      const filtered = this.features.map((e: any) => {
        const filteredObj = {}
        exportableCSVHeaders.forEach((headerKey: any) => {
          // @ts-ignore
          filteredObj[headerKey] = e[headerKey]
        })
        return filteredObj

      })

      filtered.forEach((d: any) => {
        rows.push(Object.values(d))
      });

      let csvContent = ""
      rows.forEach(function (rowArray) {
        const row = rowArray.join(";");
        csvContent += row + "\r\n";
      });

      const link = document.createElement("a");
      let timestamp = new Date()
      link.setAttribute("href", "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURI(csvContent));
      link.setAttribute("download", "export_swisslichens_" + timestamp.getFullYear() + "_" + (timestamp.getMonth() + 1) + "_" + timestamp.getDate() + ".csv");
      document.body.appendChild(link); // Required for FF

      link.click();
    },
    onPage(event: any) {
      this.lazyParams = event;
      this.loadData();
    },
    onSort(event: any) {
      this.lazyParams = event;
      this.loadData();
    },
    onInput: debounce(function (this: any, event: any) {
      this.inputText = event
    }, 500),
    onFilter() {
      // @ts-ignore
      this.lazyParams.filters = this.filters;
      this.loadData();
    },
  },
  mounted() {
    this.loading = true
    const refTable: any = this.$refs.RecordTable

    this.lazyParams = {
      first: 0,
      rows: refTable.rows,
      /*      sortField: null,
            sortOrder: null,*/
      filters: this.filters
    }

    this.loadData()


  }
})
